import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Receipt, Undo2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { money } from '@/lib/money'
import type { Order } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'

type Method = 'offline' | 'wallet'

/**
 * Settling money on an order outside checkout. 'payment' marks an order
 * produced on credit as paid; 'refund' records that money owed back on a
 * cancelled order has been returned. Neither moves money through a gateway.
 */
export default function RecordPaymentModal({
  order,
  mode,
  onClose,
}: {
  order: Order | null
  mode: 'payment' | 'refund'
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [method, setMethod] = useState<Method>('offline')
  const [reference, setReference] = useState('')
  const [amount, setAmount] = useState('')

  useEffect(() => {
    if (!order) return
    setMethod('offline')
    setReference('')
    setAmount(String(order.total ?? ''))
    // Reset only when the dialog opens on an order, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, mode])

  const amountValue = Number(amount)
  const amountOk = amountValue > 0 && amountValue <= (order?.total ?? 0)

  const save = useMutation({
    mutationFn: async () =>
      mode === 'payment'
        ? (await api.patch<{ message?: string }>(`/admin/orders/${order!.id}/payment`, {
            method,
            reference: method === 'offline' ? reference.trim() || null : null,
          })).data
        : (await api.post<{ message?: string }>(`/admin/orders/${order!.id}/refund`, {
            amount: amountValue,
            reference: reference.trim() || null,
          })).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      onClose()
      toast.success(res.message ?? (mode === 'payment' ? 'Payment recorded' : 'Refund recorded'))
    },
    onError: (err) =>
      toast.error(mode === 'payment' ? 'Could not record the payment' : 'Could not record the refund', errorMessage(err)),
  })

  const ready = mode === 'payment' || amountOk

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      title={order ? `${mode === 'payment' ? 'Record payment' : 'Record refund'} - ${order.number}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>
            {mode === 'payment' ? <Receipt className="size-4" /> : <Undo2 className="size-4" />}
            {mode === 'payment' ? 'Mark paid' : 'Record refund'}
          </Button>
        </>
      }
    >
      {order &&
        (mode === 'payment' ? (
          <div className="space-y-3">
            <p className="text-sm">
              Outstanding: <span className="font-bold">{money(order.total)}</span>
            </p>
            {(
              [
                { value: 'offline', label: 'Paid offline', hint: 'Cash, bank transfer or cheque received outside the system.' },
                {
                  value: 'wallet',
                  label: "Charge the customer's wallet",
                  hint: `Takes ${money(order.total)} from their wallet balance now. Refused if the balance is short.`,
                },
              ] as { value: Method; label: string; hint: string }[]
            ).map((choice) => (
              <label
                key={choice.value}
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-sm',
                  method === choice.value ? 'border-primary bg-primary/5' : 'border-line',
                )}
              >
                <input
                  type="radio"
                  name="payment-method"
                  checked={method === choice.value}
                  onChange={() => setMethod(choice.value)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="font-semibold">{choice.label}</span>
                  <span className="block text-xs text-muted">{choice.hint}</span>
                </span>
              </label>
            ))}
            {method === 'offline' && (
              <Field label="Reference (optional)" htmlFor="payment-reference">
                <Input
                  id="payment-reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  maxLength={100}
                  placeholder="Receipt or transfer reference"
                />
              </Field>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              Record money already returned to the customer outside the system - through the payment gateway's
              dashboard or a bank transfer. Nothing is sent from here.
            </p>
            <Field
              label="Amount refunded (RM)"
              htmlFor="refund-amount"
              error={amountOk ? undefined : `Between RM0.01 and ${money(order.total)}.`}
            >
              <Input
                id="refund-amount"
                type="number"
                min={0.01}
                step="0.01"
                max={order.total}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
            <Field label="Reference (optional)" htmlFor="refund-reference">
              <Input
                id="refund-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={100}
                placeholder="Gateway refund or transfer reference"
              />
            </Field>
          </div>
        ))}
    </Modal>
  )
}
